<template>
  <v-container fluid class="list-page">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />
    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editEvent="selectedEvent"
    />

    <div class="list-page-heading">
      <div><div class="list-page-heading__eyebrow">Community</div><h1>Events</h1><p>Create public events, share registration links, and review responses.</p></div>
      <div class="list-page-heading__actions"><v-btn v-if="checkRightStatus(69)" color="primary" depressed @click="add"><v-icon left>mdi-plus</v-icon>New event</v-btn></div>
    </div>

    <v-row class="list-metrics">
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-calendar-multiple</v-icon></div><div><div class="list-metric__value">{{ filteredEvents.length }}</div><div class="list-metric__label">Events in result</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-calendar-check-outline</v-icon></div><div><div class="list-metric__value">{{ activeEventCount }}</div><div class="list-metric__label">Active events</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-account-check-outline</v-icon></div><div><div class="list-metric__value">{{ totalSubmissions }}</div><div class="list-metric__label">Total submissions</div></div></div></v-col>
      <v-col cols="6" sm="3"><div class="list-metric"><div class="list-metric__icon"><v-icon color="primary" size="21">mdi-credit-card-outline</v-icon></div><div><div class="list-metric__value">{{ paidEventCount }}</div><div class="list-metric__label">Payment events</div></div></div></v-col>
    </v-row>

    <v-card :loading="loading" elevation="0" class="list-card">
      <div class="list-toolbar">
        <v-text-field v-model="search" class="list-toolbar__search" outlined prepend-inner-icon="mdi-magnify" dense background-color="#F8FAF8" placeholder="Search events" single-line hide-details clearable />
        <div class="list-toolbar__actions"><v-btn icon outlined color="primary" :loading="loading" aria-label="Refresh events" @click="getList"><v-icon size="19">mdi-refresh</v-icon></v-btn></div>
      </div>
      <v-data-table
        class="list-table list-table--responsive"
        :headers="headers"
        :items="Events"
        :search="search"
        :loading="loading"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :page.sync="page"
        :items-per-page="itemsPerPage"
      >
        <template v-slot:item="{ item }">
          <tr class="list-table-row">
            <td data-label="Created" @click="viewEvent(item)"><div class="list-cell-primary">{{ item.createdAt | moment("M/D/YYYY") }}</div></td>
            <td data-label="Event" @click="viewEvent(item)">
              <div class="list-identity"><div class="list-identity__icon"><v-icon color="primary" size="19">mdi-calendar-star</v-icon></div><div class="list-identity__content"><div class="list-identity__title">{{ item.name }}</div><div class="list-identity__subtitle">{{ item.active ? "Registration active" : "Registration inactive" }}</div></div></div>
            </td>
            <td data-label="Public link">
              <a class="event-link" :href="eventPublicUrl(item)" target="_blank" rel="noopener" @click.stop>{{ eventPublicUrl(item) }}<v-icon size="15" color="primary">mdi-open-in-new</v-icon></a>
            </td>
            <td data-label="Submissions" class="text-center" @click="viewEvent(item)"><span class="list-count">{{ item.submitCount || 0 }}</span></td>
            <td data-label="Actions" class="justify-end align-center text-right action-cell">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" icon>
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list class="menu-button">
                  <v-list-item
                    @click="viewEvent(item)"
                  >
                    <v-list-item-title>View</v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item
                    v-if="checkRightStatus(71)"
                    @click="editEvent(item)"
                  >
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="checkRightStatus(72)"
                    @click="deleteEvent(item)"
                  >
                    <v-list-item-title>Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
        <template v-slot:no-data><div class="list-empty-state py-12"><v-icon size="48" color="grey lighten-1">mdi-calendar-search-outline</v-icon><strong>No events found</strong><span>{{ search ? "Try a different event name." : "Create an event to begin collecting registrations." }}</span></div></template>
        <template v-slot:no-results><div class="list-empty-state py-12"><v-icon size="48" color="grey lighten-1">mdi-calendar-search-outline</v-icon><strong>No events found</strong><span>Try a different event name.</span></div></template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import EventService from "../service";
import { mapGetters } from "vuex";
import Hashids from "hashids";
const hashids = new Hashids();

const Create = () => import("./Create");
const Edit = () => import("./Edit");
export default {
  name: "Event-list",
  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedEvent: null,
      headers: [
        {
          text: 'Created',
          align: "left",
          value: "createdAt",
          sortable: true,
        },
        {
          text: "Event",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Public Link",
          align: "left",
          value: "id",
          sortable: false,
        },
        {
          text: "Submissions",
          align: "center",
          value: "submitCount",
          sortable: false,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      currentEnvironment: process.env.VUE_APP_NODE_ENV || 'Unknown',
      Events: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "createdAt",
      sortDesc: true,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  created() {
    this.getList();
    document.title = "taleem - Event List";
  },
  components: {
    Create,
    Edit,
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
    filteredEvents() {
      if (!this.search) return this.Events;
      const search = this.search.toLowerCase();
      return this.Events.filter((event) => (event.name || "").toLowerCase().includes(search));
    },
    activeEventCount() {
      return this.filteredEvents.filter((event) => event.active).length;
    },
    totalSubmissions() {
      return this.filteredEvents.reduce((total, event) => total + (Number(event.submitCount) || 0), 0);
    },
    paidEventCount() {
      return this.filteredEvents.filter((event) => event.paymentRequired || Number(event.amount) > 0).length;
    },
  },
  methods: {
    add() {
      this.createDialog = !this.createDialog;
    },
    async getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;
      try {
        const response = await EventService.getAll();
        this.Events = (response.data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return response;
      } finally {
        this.loading = false;
      }
    },
    eventPublicUrl(event) {
      return `https://portal.taleem.app/e/${event.id}`;
    },
    editEvent(Event) {
      this.selectedEvent = Event;
      this.editDialog = !this.editDialog;
    },
    viewEvent(event){
      const hashId = hashids.encode(event.id);
      const routeData = this.$router.resolve({
        path: `/global/events/${hashId}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    deleteEvent(Event) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          EventService.delete(Event.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Event has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.event-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 330px;
  overflow: hidden;
  color: #2e7d32;
  font-weight: 600;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-link:hover { text-decoration: underline; }
</style>
